// Native swipe/scroll carousel with scroll snapping, arrow controls, seamless
// wrapping, and autoplay. Custom element, so it re-initialises on its own when
// the theme editor re-renders the section.
//
// Wrapping: the cards are cloned once before and once after the originals.
// Whenever scrolling settles inside a clone set, the track jumps (instantly)
// to the matching card in the original set, which looks identical.
if (!customElements.get("testimonials-carousel")) {
  customElements.define(
    "testimonials-carousel",
    class TestimonialsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector(".testimonials__track");
        this.previous = this.querySelector(".testimonials__button--prev");
        this.next = this.querySelector(".testimonials__button--next");
        this.controls = this.querySelector(".testimonials__controls");
        this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
        this.delay = Math.max(1, parseFloat(this.dataset.autoplaySpeed) || 4) * 1000;

        this.track.querySelectorAll("[data-clone]").forEach((clone) => clone.remove());
        this.originals = [...this.track.querySelectorAll(".testimonials__review")];
        this.count = this.originals.length;
        // Keep the saved order in the theme editor, where the section re-renders on every change.
        if (!window.Shopify?.designMode) this.shuffle();
        if (this.count > 1) this.addClones();
        this.cards = [...this.track.querySelectorAll(".testimonials__review")];

        this.looping = false;
        this.logicalIndex = 0;
        this.targetIndex = null;
        this.timer = 0;
        this.settleTimer = 0;
        this.touching = false;
        this.pauses = new Set();

        this.bindEvents();
        this.layout();
      }

      disconnectedCallback() {
        this.unbindEvents();
        clearTimeout(this.timer);
        clearTimeout(this.settleTimer);
      }

      // Fisher–Yates shuffle, so each page load shows the reviews in a new order.
      shuffle() {
        for (let i = this.count - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [this.originals[i], this.originals[j]] = [this.originals[j], this.originals[i]];
        }
        this.track.append(...this.originals);
        this.originals.forEach((card, index) =>
          card.setAttribute("aria-label", `Review ${index + 1} of ${this.count}`)
        );
      }

      addClones() {
        const clone = (card) => {
          const copy = card.cloneNode(true);
          [...copy.attributes]
            .filter(({ name }) => name.startsWith("data-shopify"))
            .forEach(({ name }) => copy.removeAttribute(name));
          copy.dataset.clone = "";
          copy.setAttribute("aria-hidden", "true");
          copy.inert = true;
          return copy;
        };
        this.track.prepend(...this.originals.map(clone));
        this.track.append(...this.originals.map(clone));
      }

      bindEvents() {
        this.listeners = [
          [this.previous, "click", () => this.move(-1)],
          [this.next, "click", () => this.move(1)],
          [this, "mouseenter", () => this.setPaused("hover", true)],
          [this, "mouseleave", () => this.setPaused("hover", false)],
          [this, "focusin", () => this.setPaused("focus", true)],
          [this, "focusout", (event) => {
            if (!this.contains(event.relatedTarget)) this.setPaused("focus", false);
          }],
          [this.track, "wheel", () => (this.targetIndex = null)],
          [this.track, "touchstart", () => {
            this.touching = true;
            this.targetIndex = null;
            this.setPaused("touch", true);
          }],
          [this.track, "touchend", () => {
            this.touching = false;
            this.setPaused("touch", false);
            this.scheduleSettle();
          }],
          [document, "visibilitychange", () => this.setPaused("hidden", document.hidden)],
          [document, "shopify:block:select", (event) => this.onBlockSelect(event)],
          [document, "shopify:block:deselect", (event) => {
            if (this.contains(event.target)) this.setPaused("editor", false);
          }],
          [this.reducedMotion, "change", () => this.restartAutoplay()],
        ];
        if ("onscrollend" in window) {
          this.listeners.push([this.track, "scrollend", () => this.settle()]);
        } else {
          this.listeners.push([this.track, "scroll", () => this.scheduleSettle()]);
        }
        this.listeners.forEach(([target, type, handler]) =>
          target.addEventListener(type, handler, { passive: true })
        );

        this.resizeObserver = new ResizeObserver(() => this.layout());
        this.resizeObserver.observe(this.track);
        this.intersectionObserver = new IntersectionObserver(([entry]) =>
          this.setPaused("offscreen", !entry.isIntersecting)
        );
        this.intersectionObserver.observe(this);
      }

      unbindEvents() {
        this.listeners.forEach(([target, type, handler]) =>
          target.removeEventListener(type, handler)
        );
        this.resizeObserver.disconnect();
        this.intersectionObserver.disconnect();
      }

      // Scroll position at which a card snaps to the start of the track.
      snapPosition(card) {
        const scrollPadding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
        return (
          card.getBoundingClientRect().left -
          this.track.getBoundingClientRect().left +
          this.track.scrollLeft -
          scrollPadding
        );
      }

      nearestIndex() {
        let nearest = 0;
        let distance = Infinity;
        this.cards.forEach((card, index) => {
          const offset = Math.abs(this.snapPosition(card) - this.track.scrollLeft);
          if (offset < distance) {
            distance = offset;
            nearest = index;
          }
        });
        return nearest;
      }

      jumpTo(card) {
        this.track.scrollTo({ left: this.snapPosition(card), behavior: "auto" });
      }

      // Only wrap and autoplay when the cards don't all fit on screen.
      layout() {
        if (!this.count) {
          this.controls.hidden = true;
          return;
        }
        const style = getComputedStyle(this.track);
        const contentWidth =
          this.track.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const first = this.originals[0].getBoundingClientRect();
        const last = this.originals[this.count - 1].getBoundingClientRect();

        this.looping = this.count > 1 && last.right - first.left > contentWidth + 1;
        this.classList.toggle("testimonials--static", !this.looping);
        this.controls.hidden = !this.looping;
        this.targetIndex = null;
        this.jumpTo(this.looping ? this.originals[this.logicalIndex] : this.originals[0]);
        this.restartAutoplay();
      }

      move(direction) {
        if (!this.looping) return;
        const from = this.targetIndex ?? this.nearestIndex();
        this.targetIndex = Math.min(this.cards.length - 1, Math.max(0, from + direction));
        this.track.scrollTo({
          left: this.snapPosition(this.cards[this.targetIndex]),
          behavior: this.reducedMotion.matches ? "auto" : "smooth",
        });
      }

      scheduleSettle() {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 150);
      }

      // Once scrolling stops, move from a clone set back into the originals.
      settle() {
        // Chrome fires scrollend when one smooth scroll interrupts another;
        // ignore it until the arrow/autoplay target is actually reached.
        if (this.targetIndex !== null) {
          const target = this.snapPosition(this.cards[this.targetIndex]);
          if (Math.abs(target - this.track.scrollLeft) > 2) return;
        }
        this.targetIndex = null;
        if (!this.looping || this.touching) return;
        const index = this.nearestIndex();
        this.logicalIndex = index % this.count;
        if (index < this.count || index >= this.count * 2) {
          this.jumpTo(this.originals[this.logicalIndex]);
        }
      }

      onBlockSelect(event) {
        const card = event.target.closest?.(".testimonials__review");
        if (!card || !this.contains(card)) return;
        this.setPaused("editor", true);
        if (this.looping) this.track.scrollTo({ left: this.snapPosition(card), behavior: "smooth" });
      }

      setPaused(reason, paused) {
        if (paused === this.pauses.has(reason)) return;
        if (paused) {
          this.pauses.add(reason);
        } else {
          this.pauses.delete(reason);
          this.restartAutoplay();
        }
      }

      restartAutoplay() {
        clearTimeout(this.timer);
        if (!this.looping || this.reducedMotion.matches) return;
        this.timer = setTimeout(() => {
          if (!this.pauses.size) this.move(1);
          this.restartAutoplay();
        }, this.delay);
      }
    }
  );
}
